import { useEffect, useMemo, useState } from 'react'
import { searchCommands, shortcutHelp, type WorkspaceCommand, type WorkspaceHost, type WorkspaceMode, type WorkspacePlatform } from '@/lib/canvas/workspace-commands'

/** Help and action search drawn from the workspace command registry. */
export function WorkspaceCommandDialog({ mode, host, platform, selectionCount, searching, onRun, onClose }: {
  mode: WorkspaceMode
  host: WorkspaceHost
  platform: WorkspacePlatform
  selectionCount: number
  searching: boolean
  onRun: (command: WorkspaceCommand) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const rows = useMemo(() => searching
    ? searchCommands(host, mode, query, selectionCount).map(command => ({ id: command.id, keys: platform === 'mac' ? command.shortcutMac : command.shortcutOther, description: command.label, available: true, command }))
    : shortcutHelp(host, mode, platform, selectionCount).flatMap(group => group.shortcuts.map(row => ({ ...row, command: null as WorkspaceCommand | null }))),
  [searching, host, mode, query, selectionCount, platform])
  const runnable = searching ? searchCommands(host, mode, query, selectionCount) : []
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]" role="dialog" aria-modal="true" aria-label={searching ? 'Action search' : 'Keyboard shortcuts'}>
    <button type="button" className="absolute inset-0 bg-black/60" aria-label="Close" onClick={onClose} />
    <div className="relative z-10 w-full max-w-lg max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-float mx-4">
      <div className="sticky top-0 bg-popover border-b border-border px-4 py-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{searching ? 'Actions' : 'Keyboard shortcuts'}</h2>
        <button type="button" className="text-sm text-muted-foreground" onClick={onClose}>Close</button>
      </div>
      {searching && <div className="px-4 py-3 border-b border-border">
        <input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search actions" aria-label="Search actions" className="w-full bg-transparent text-sm outline-none" onKeyDown={event => {
          if (event.key === 'Enter' && runnable[0]) { event.preventDefault(); onRun(runnable[0]); onClose() }
        }} />
      </div>}
      <ul className="px-4 py-3 space-y-2">
        {searching ? runnable.map(command => <li key={command.id}>
          <button type="button" className="flex w-full items-center justify-between gap-3 text-sm" onClick={() => { onRun(command); onClose() }}>
            <span>{command.label}</span>
            <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">{platform === 'mac' ? command.shortcutMac : command.shortcutOther}</kbd>
          </button>
        </li>) : rows.map(row => <li key={row.id} className="flex items-center justify-between gap-3 text-sm">
          <span className={row.available ? '' : 'text-muted-foreground'}>{row.description}{row.available ? '' : ' · unavailable'}</span>
          <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">{row.keys}</kbd>
        </li>)}
        {rows.length === 0 && <li className="text-sm text-muted-foreground">No matching actions</li>}
      </ul>
    </div>
  </div>
}
